import { useState, useEffect } from 'react';
import { Email } from '../types/email';
import { Modal, Paper, Box, Typography, Button, Grid, Chip, Avatar, ToggleButtonGroup, ToggleButton, Card, List, ListItem } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { styled, alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachmentIcon from '@mui/icons-material/Attachment';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { trackEvent } from '../utils/analytics'; // Import the trackEvent function

interface EmailContentProps {
  email: Email | null;
  onClose?: () => void;
}

// Custom styled components
const StyledModal = styled(Modal)((_theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(8px)'
}));

const EmailPaper = styled(Paper)(({ theme }) => ({
  width: '90%',
  maxWidth: 900,
  height: '85vh',
  maxHeight: 800,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 16,
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`
}));

const EmailHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: alpha(theme.palette.background.paper, 0.4)
}));

const GradientText = styled(Typography)<TypographyProps>({
  background: 'linear-gradient(45deg, #646cff, #9e9eff)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 600
});

function EmailContent({ email, onClose }: EmailContentProps) {
  const [showHtml, setShowHtml] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const theme = useTheme();
  
  useEffect(() => {
    if (email) {
      setTimeout(() => {
        setIsVisible(true);
        // Track email open event with GA4
        trackEvent(
          'Email Interaction', 
          'Open Email', 
          `Subject: ${email.subject}`, 
          // Optionally add email.id as value if available
        );
      }, 50);
    } else {
      setIsVisible(false);
    }
  }, [email]);
  
  if (!email) {
    return null;
  }

  const formattedDate = new Date(email.date).toLocaleString();

  const handleClose = () => {
    setIsVisible(false);
    // Track email close event
    trackEvent('Email Interaction', 'Close Email', `Subject: ${email.subject}`);
    setTimeout(() => onClose && onClose(), 300);
  };

  // Handler for view toggle with analytics
  const handleViewToggle = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value !== null) {
      const newView = value === 'html';
      setShowHtml(newView);
      // Track view toggle event
      trackEvent(
        'Email Interaction',
        'Toggle View',
        `Changed to ${newView ? 'HTML' : 'Text'} view`,
      );
    }
  };

  // Track attachment clicks
  const handleAttachmentClick = (attachmentName: string) => {
    trackEvent(
      'Email Interaction',
      'Click Attachment',
      attachmentName
    );
    // Your existing attachment click handler logic
  };

  return (
    <StyledModal
      open={isVisible}
      onClose={handleClose}
      aria-labelledby="email-modal-title"
    >
      <EmailPaper elevation={6} onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <EmailHeader>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleClose}
                size="small"
                sx={{ borderRadius: 20 }}
              >
                Back
              </Button>
              
              {email.htmlBody && (
                <ToggleButtonGroup
                  value={showHtml ? 'html' : 'text'}
                  exclusive
                  size="small"
                  onChange={handleViewToggle}
                  aria-label="view format"
                >
                  <ToggleButton value="html">HTML</ToggleButton>
                  <ToggleButton value="text">Text</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
            
            <GradientText variant="h4" component="h2" id="email-modal-title" sx={{ mb: 2 }}>
              {email.subject}
            </GradientText>
            
            <Grid container spacing={2} alignItems="center">
              <Grid>
                <Avatar sx={{ 
                  background: 'linear-gradient(45deg, #646cff, #9e9eff)',
                  width: 40, 
                  height: 40
                }}>
                  {email.from.charAt(0).toUpperCase()}
                </Avatar>
              </Grid>
              <Grid size="grow">
                <Typography variant="subtitle1">{email.from}</Typography>
                <Typography variant="body2" color="text.secondary">
                  To: <Box component="span" sx={{ color: 'text.primary' }}>{email.to}</Box>
                </Typography>
              </Grid>
              <Grid>
                <Chip 
                  label={formattedDate} 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    borderRadius: 4, 
                    background: alpha(theme.palette.background.paper, 0.3)
                  }} 
                />
              </Grid>
            </Grid>
          </EmailHeader>
          
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 3,
            bgcolor: alpha(theme.palette.background.paper, 0.2)
          }}>
            <Card variant="outlined" sx={{ p: 2 }}>
              {showHtml && email.htmlBody ? (
                <Box 
                  className="email-html-body"
                  sx={{ 
                    '& a': { color: 'primary.main' },
                    '& img': { maxWidth: '100%' }
                  }}
                  dangerouslySetInnerHTML={{ __html: email.htmlBody }}
                />
              ) : (
                <Box sx={{ lineHeight: 1.6 }}>
                  {email.body.split('\n').map((line, i) => (
                    <Typography key={i} paragraph={true} sx={{ my: line ? 1 : 0.5 }}>
                      {line || <br />}
                    </Typography>
                  ))}
                </Box>
              )}
            </Card>
          </Box>
          
          {email.attachments && email.attachments.length > 0 && (
            <Box sx={{ 
              p: 2, 
              borderTop: 1, 
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 0.4)
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <AttachmentIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Attachments ({email.attachments.length})
                </Typography>
              </Box>
              
              <List dense disablePadding>
                {email.attachments.map((attachment, index) => (
                  <ListItem 
                    key={index}
                    component={Chip}
                    icon={<InsertDriveFileIcon fontSize="small" />}
                    label={attachment}
                    clickable
                    onClick={() => handleAttachmentClick(attachment)}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </List>
            </Box>
          )}
        </Box>
      </EmailPaper>
    </StyledModal>
  );
}

export default EmailContent;